import React, {
  useState, useEffect, useRef, useCallback, memo
} from "react";
import { useLocation } from "react-router-dom";
import TopSection from "./TopSection";
import "./ImageGallery.css";

import JSZip   from "jszip";
import { saveAs } from "file-saver";

/* ════════════════ constants ════════════════ */
const LONG_MS             = 500;   // ms for long-press
const MAX_DL_AT_ONCE      = 30;    // מעל זה – ZIP
const MAX_SHARE_AT_ONCE   = 10;
const ZIP_CONC            = 6;     // כמה במקביל ל-fetch ל-ZIP

/* ════════════════ helpers ════════════════ */
const isShareCancelled = e =>
  e?.name==="AbortError" || e?.name==="NotAllowedError" ||
  (e instanceof DOMException && e.code===20);

const extractSimpleName = (url="")=>{
  try{ return new URL(url).pathname.split("/").pop(); }
  catch{ return ""; }
};

const uniqueByName = urls=>{
  const seen=new Set();
  return urls.filter(img=>{
    const n=extractSimpleName(img.url_small||img.url_origin).toLowerCase();
    if(seen.has(n)) return false;
    seen.add(n); return true;
  });
};

const downloadFile = (url,name)=>{
  const a=document.createElement("a");
  a.href=url; a.download=name;
  document.body.appendChild(a); a.click(); a.remove();
};

/* ════════════════ memoised item ════════════════ */
const Item = memo(({ img, idx, marked, onDown, onUp, onLeave })=>(
  <div
    className={`image-item ${marked?"selected":""}`}
    onPointerDown={e=>onDown(e,idx)}
    onPointerUp={e=>onUp(e,idx)}
    onPointerLeave={onLeave}
  >
    <img src={img.url_small} alt=""/>
    {marked&&<img src="/icons/v.png" className="selected-icon" alt=""/>}
  </div>
));

/* ════════════════ main component ════════════════ */
export default function ImageGallery(){
  /* router data */
  const { state } = useLocation();
  const urls      = state?.urls??[];
  const userData  = state?.userData??null;
const viewerOpenTs = useRef(0);

  /* gallery state */
  const [images,setImages]=useState([]);
  const [sel,setSel]      =useState([]); // indices
  const [mode,setMode]    =useState(false);
  const [expand,setExpand]=useState(false);

  /* viewer */
  const [viewer,setViewer]=useState({ open:false,idx:0 });
  const [vLoaded,setVLoaded]=useState(false);

  /* popup */
  const [popup,setPopup]=useState({ show:false, txt:"", progress:0 });
  const openPop =(txt,progress=0)=>setPopup({ show:true, txt, progress });
  const updPop  =(txt,progress)=>setPopup({ show:true, txt, progress });
  const closePop=()=>setPopup({ show:false, txt:"", progress:0 });

  /* refs */
  const longRef = useRef(false);
  const tRef    = useRef(null);
  const modeRef = useRef(false);
  useEffect(()=>{ modeRef.current=mode; },[mode]);

  /* init unique images */
  useEffect(()=>setImages(uniqueByName(urls)),[urls]);

  /* ---------- selection logic ---------- */
  const toggle = useCallback(idx=>{
    setSel(prev=>{
      const next = prev.includes(idx) ? prev.filter(x=>x!==idx) : [...prev,idx];
      const active=!!next.length; setMode(active); modeRef.current=active;
      return next;
    });
  },[]);

  const selectAll = ()=>{
    setSel(prev=>{
      const next = prev.length===images.length ? [] : images.map((_,i)=>i);
      const active=!!next.length; setMode(active); modeRef.current=active;
      return next;
    });
  };

  /* ---------- pointer handlers ---------- */
  const onDown = (_,idx)=>{
    longRef.current=false;
    tRef.current=setTimeout(()=>{
      longRef.current=true; toggle(idx);
      setMode(true); modeRef.current=true;
    },LONG_MS);
  };
  const onUp = (_, idx) => {
  clearTimeout(tRef.current);
  if (longRef.current) return;

  if (modeRef.current) toggle(idx);
  else {
    viewerOpenTs.current = performance.now();     // ← חדש
    requestAnimationFrame(() => {                 // מונע קליק כפול במובייל
      setViewer({ open: true, idx });
      setVLoaded(false);
    });
  }
};

  const onLeave = ()=>clearTimeout(tRef.current);

  /* ---------- viewer nav ---------- */
  const next = ()=>setViewer(v=>({...v,idx:(v.idx+1)%images.length}));
  const prev = ()=>setViewer(v=>({...v,idx:(v.idx-1+images.length)%images.length}));
  useEffect(()=>{
    if(!viewer.open) return;
    const h=e=>{
      if(e.key==="ArrowRight") next();
      if(e.key==="ArrowLeft")  prev();
      if(e.key==="Escape")     setViewer(v=>({...v,open:false}));
    };
    window.addEventListener("keydown",h);
    return ()=>window.removeEventListener("keydown",h);
  },[viewer.open,images.length]);

  /* ════════════════ DOWNLOAD / ZIP ════════════════ */
  const buildZip = async list=>{
    openPop("מכין ZIP…",0);
    try{
      const zip=new JSZip();
      let done=0;
      for(let i=0;i<list.length;i+=ZIP_CONC){
        const slice=list.slice(i,i+ZIP_CONC);
        const blobs=await Promise.all(slice.map(async img=>{
          const r=await fetch(img.url_origin,{ mode:"cors" });
          return r.blob();
        }));
        blobs.forEach((b,j)=>{
          const idx=i+j+1;
          const ext=(b.type.split("/")[1]||"jpg").split(";")[0];
          zip.file(`heyi_${idx}.${ext}`,b);
          done++;
        });
        updPop(`אוסף ${done}/${list.length}…`,Math.round((done/list.length)*100));
      }
      const blob=await zip.generateAsync({ type:"blob" });
      saveAs(blob,"heyi_images.zip");
      updPop("✔️ ZIP מוכן!",100);
      setTimeout(closePop,3000);
    }catch(e){
      console.error(e); openPop("❌ שגיאה ב-ZIP");
    }
  };

  const handleDownload = ()=>{
    if(!sel.length) return openPop("לא נבחרו תמונות להורדה.");
    const total=sel.length;

    /* ZIP מעל 30 */
    if(total>MAX_DL_AT_ONCE){
      buildZip(sel.map(i=>images[i]));
      return;
    }

    /* otherwise – staggered downloads with pause */
    openPop(`מוריד 0 מתוך ${total}…`,0);
    sel.forEach((idx,i)=>{
      const img=images[idx];
      if(!img?.url_origin) return;
      const delay=1750*i + Math.floor(i/10)*10000; // pause every 10
      setTimeout(()=>{
        downloadFile(img.url_origin,`image_${i+1}.jpg`);
        const done=i+1; updPop(`מוריד ${done} מתוך ${total}…`,Math.round(done/total*100));
        if(done===total) setTimeout(closePop,3000);
      },delay);
    });
  };

  /* ════════════════ SHARE ════════════════ */
  const handleShare = async ()=>{
    /* limit */
    if(sel.length>MAX_SHARE_AT_ONCE)
      return openPop(`אפשר לשתף עד ${MAX_SHARE_AT_ONCE} תמונות.`);

    /* share link fallback */
    if(!sel.length){
      try{
        if(navigator.share){
          await navigator.share({
            title:"Heyi Gallery", text:"קבלו את התמונות שלי 👇", url:window.location.href
          });
        }else{
          await navigator.clipboard.writeText(window.location.href);
          openPop("הקישור הועתק!");
        }
      }catch(e){
        if(!isShareCancelled(e)){
          console.error(e);
          await navigator.clipboard.writeText(window.location.href);
          openPop("הקישור הועתק!");
        }
      }
      return;
    }

    /* share selected files */
    openPop("מכין קבצים לשיתוף…",0);
    try{
      const files=await Promise.all(
        sel.map(async(idx,i)=>{
          const r=await fetch(images[idx].url_origin,{ mode:"cors" });
          let blob=await r.blob();
          if(!blob.type.startsWith("image/"))
            blob=new Blob([blob],{ type:"image/jpeg" });
          const ext=blob.type.split("/")[1];
          return new File([blob],`heyi_${i+1}.${ext}`,{ type:blob.type });
        })
      );
      const payload={ title:"Heyi Gallery", files };
      if(navigator.canShare?.(payload) && navigator.share){
        await navigator.share(payload);
        closePop();
      }else openPop("הדפדפן לא תומך בשיתוף קבצים.");
    }catch(err){
      if(!isShareCancelled(err)){ console.error(err); openPop("❌ שגיאה בשיתוף"); }
      else closePop();
    }
  };

  /* ════════════════ render ════════════════ */
  const count=sel.length;
  const all  =count===images.length && images.length;

  return(
    <div className="main-container">
      {/* header */}
      <TopSection
        userData={userData}
        title={
          images.length
            ? "קיבלתם את התמונות\nביכולתכם להוריד או לשתף אותן\nתהנו :)"
            : "לא נמצאו תמונות :("
        }
      />

      {/* middle bar */}
      <div className="middle-section">
        {mode?(
          <>
            <div className="selected-count">{count} נבחרו</div>
            <button className="select-all-btn" onClick={selectAll}>
              <span className={`circular-checkbox ${all?"checked":""}`}/>
              {all?"בטל בחירת הכל":"בחר הכל"}
            </button>
          </>
        ):(
          <>
            <div className="gallery-text-images2">
              <div className="gallery-text">גלריה</div>
              <div className="gallery-text-images">{images.length} תמונות</div>
            </div>
            {images.length>0&&(
              <button className="gallery-toggle-btn" onClick={()=>setExpand(p=>!p)}>
                {expand?"הצג פחות":"הצג הכל"}
              </button>
            )}
          </>
        )}
      </div>

      {/* gallery */}
      <div className={`bottom-section ${expand?"expanded":""}`}>
        <div className="gallery-container">
          {images.length===0?(
            <div className="no-images-message">
              נסו זווית אחרת או תמונה ברורה יותר :)
            </div>
          ):(
            <div className="image-gallery">
              {images.map((img,idx)=>(
                <Item
                  key={idx}
                  img={img}
                  idx={idx}
                  marked={sel.includes(idx)}
                  onDown={onDown}
                  onUp={onUp}
                  onLeave={onLeave}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* action bar */}
      {/* <div className={`action-bar-guest ${images.length===0?"disabled":""}`}>
        {mode?(
          <>
            <button className="action-item-guest" onClick={handleDownload}>
              <img src="/icons/download.png" className="action-icon" alt=""/><span>הורד</span>
            </button>
          </>
        ):(
          <>
            <button className="action-item-guest" onClick={handleShare}>
              <img src="/icons/share.png" className="action-icon" alt=""/><span>שתף</span>
            </button>
            <button
              className="action-item-guest"
              onClick={()=>openPop("הורדת התמונות התחילה, המתן בבקשה.")}
            >
              <img src="/icons/download.png" className="action-icon" alt=""/><span>הורדה</span>
            </button>
          </>
        )}
      </div> */}

      {/* viewer */}
      {viewer.open&&(
        <div className="viewer-overlay" onClick={() => {
      if (performance.now() - viewerOpenTs.current < 250) return; // ← חדש
      setViewer(v => ({ ...v, open: false }));
    }}>
          <div className="viewer-inner" onClick={e=>e.stopPropagation()}>
            <img
              src={images[viewer.idx]?.url_small}
              className="viewer-image"
              alt=""
              onLoad={()=>setVLoaded(true)}
            />
            <button className="viewer-next" onClick={next}>›</button>
            <button className="viewer-prev" onClick={prev}>‹</button>
            <button className="viewer-close" onClick={()=>setViewer(v=>({...v,open:false}))}>×</button>
            <div className="viewer-actions">
              <button onClick={()=>downloadFile(images[viewer.idx].url_origin,"image.jpg")} disabled={!vLoaded}>
                <img src="/icons/download.png" alt=""/> הורד
              </button>
              <button onClick={()=>handleShare([viewer.idx])} disabled={!vLoaded}>
                <img src="/icons/share.png" alt=""/> שתף
              </button>
            </div>
          </div>
        </div>
      )}

      {/* popup */}
      {popup.show&&(
        <div className="popup-overlay">
          <div className="popup">
            <button className="popup-close-btn" onClick={closePop}>×</button>
            <div className="popup-content">
              <span className="popupText" style={{whiteSpace:"pre-line"}}>{popup.txt}</span>
              {popup.progress>0 && popup.progress<100 && (
                <div className="progress-container">
                  <div className="progress-bar" style={{width:`${popup.progress}%`}}/>
                  <span className="progress-text">{popup.progress}%</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
